import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import { RegistrationApi, CourseRegistrationRequest, RegistrationResponse, Registration, StudentRegistrationListResponse } from '../services/registrationApi';

export interface RegistrationState {
  // Current registration form data
  currentRegistration: RegistrationResponse | null;
  
  // User's registrations list
  myRegistrations: Registration[];
  registrationsPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  
  // Loading states
  isCreatingRegistration: boolean;
  isLoadingRegistrations: boolean;
  
  // Error states
  registrationError: string | null;
  
  // Actions
  createRegistration: (request: CourseRegistrationRequest) => Promise<RegistrationResponse>;
  loadMyRegistrations: (page?: number, limit?: number) => Promise<void>;
  clearRegistrationError: () => void;
  clearCurrentRegistration: () => void;
  reset: () => void;
}

const initialState = {
  currentRegistration: null,
  myRegistrations: [],
  registrationsPagination: null,
  isCreatingRegistration: false,
  isLoadingRegistrations: false,
  registrationError: null,
};

export const useRegistrationStore = create<RegistrationState>()(
  persist(
    (set, get) => ({
      ...initialState,

      createRegistration: async (request: CourseRegistrationRequest): Promise<RegistrationResponse> => {
        set({ isCreatingRegistration: true, registrationError: null });
        
        try {
          const response = await RegistrationApi.createRegistration(request);
          
          set({ 
            currentRegistration: response,
            isCreatingRegistration: false,
            registrationError: null 
          });
          
          // Reload registrations to include the new one
          const state = get();
          if (state.myRegistrations.length > 0) {
            await state.loadMyRegistrations();
          }
          
          return response;
        } catch (error) {
          let errorMessage = 'Registration failed';
          if (isAxiosError(error)) {
            const data = error.response?.data as { message?: string; errors?: string[] } | undefined;
            if (data?.message && typeof data.message === 'string') {
              errorMessage = data.message;
            } else if (Array.isArray(data?.errors) && data.errors.length > 0) {
              errorMessage = data.errors.join(', ');
            } else if (error.message) {
              errorMessage = error.message;
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }
          set({
            isCreatingRegistration: false,
            registrationError: errorMessage,
            currentRegistration: null
          });
          throw error;
        }
      },

      loadMyRegistrations: async (page = 1, limit = 10): Promise<void> => {
        set({ isLoadingRegistrations: true, registrationError: null });
        
        try {
          const response: StudentRegistrationListResponse = await RegistrationApi.getMyRegistrations(page, limit);
          
          set({
            myRegistrations: response.data,
            registrationsPagination: response.pagination,
            isLoadingRegistrations: false,
            registrationError: null
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to load registrations';
          set({ 
            isLoadingRegistrations: false, 
            registrationError: errorMessage 
          });
          throw error;
        }
      },

      clearRegistrationError: () => {
        set({ registrationError: null });
      },

      clearCurrentRegistration: () => {
        set({ currentRegistration: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'registration-storage',
      partialize: (state) => ({
        // Only persist the registrations data, not loading states or errors
        myRegistrations: state.myRegistrations,
        registrationsPagination: state.registrationsPagination,
      }),
    }
  )
);

export default useRegistrationStore;